import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { DataService } from 'src/app/services/data.service';
import { SiteService } from 'src/app/services/site.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { DripProgram, DripProgramDetails, Campaign, EmailType } from 'src/app/models/communication/campaign';
import { forkJoin, Observable } from 'rxjs';
import { ConfirmationService } from 'primeng/api';
import { IPendingChangesComponent } from 'src/app/guards/pending-changes.guard';

interface DripProgramEmail {
	campaignID: string;
	campaignName: string;
	sequence: number;
	interval: number;
	catchup: boolean;
}

interface DripProgramModel {
	startDate: Date | null;
	deliveryTime: Date | null;
	pause: boolean;
	daysBetweenCatchup: number;
}

@Component({
  selector: 'app-dripprogram',
  templateUrl: './dripprogram.component.html',
  styleUrls: ['./dripprogram.component.scss']
})
export class DripprogramComponent implements OnInit, IPendingChangesComponent {

	public dripProgram: DripProgram = new DripProgram();
	public dripProgramID: string = '';
	public emails: DripProgramEmail[] = [];
	public availableCampaigns: Campaign[] = [];
	public loading: boolean = false;
	public deliveryTimeDate: Date | null = null; // Separate property for UI binding
	public showCampaignDialog: boolean = false;
	public filteredAvailableCampaigns: Campaign[] = [];
	public campaignSearchTerm: string = "";
	public isDirty: boolean = false;

	public dripProgramOptions = [
		{ label: 'Main', value: 'main' },
		{ label: 'Onboarding', value: 'onboarding' },
		{ label: 'Follow-up', value: 'followup' }
	];

	public selectedDripProgram = this.dripProgramOptions[0]; // Default to "Main"

	constructor(private dataService: DataService, private siteService: SiteService, private router: Router, private confirmationService: ConfirmationService) { }

	ngOnInit() {
		this.siteService.pageTitle = 'Drip Program';
		
		this.siteService.headerButtons = [
			{ text: 'Save', class: 'success', callback: () => this.save() },
			{ text: 'Add Campaign', class: 'primary', callback: () => this.showAddCampaignDialog() }
		];

		this.loadDripProgram();
	}

	loadDripProgram() {
		this.loading = true;
		
		// First get the default drip program ID
		this.dataService.getDefaultDripProgramConfig().subscribe({
			next: (config) => {
				this.dripProgramID = config.defaultDripProgramID;
				
				// Then load the drip program and its details, plus available campaigns
				forkJoin({
					dripProgram: this.dataService.getDripProgram(this.dripProgramID),
					dripProgramDetails: this.dataService.getDripProgramDetails(this.dripProgramID),
					campaigns: this.dataService.getCampaigns(undefined, undefined, EmailType.Drip)
				}).subscribe({
					next: (data) => {
						console.log('Loaded drip program data:', data.dripProgram);
						this.dripProgram = new DripProgram(data.dripProgram);
						this.availableCampaigns = data.campaigns;
						this.mapDetailsToEmails(data.dripProgramDetails);
						console.log('After mapping - dripProgram.startDate:', this.dripProgram.startDate);
						this.loading = false;
					},
					error: (error) => {
						console.error('Error loading drip program details:', error);
						this.siteService.setMessage('Error loading drip program', 'error');
						this.loading = false;
					}
				});
			},
			error: (error) => {
				console.error('Error getting default drip program config:', error);
				this.siteService.setMessage('Error loading drip program configuration', 'error');
				this.loading = false;
			}
		});
	}

	mapDetailsToEmails(details: DripProgramDetails[]) {
		this.emails = details
			.sort((a, b) => a.sequence - b.sequence)
			.map(detail => {
				const campaign = this.availableCampaigns.find(c => c.campaignID === detail.campaignID);
				return {
					campaignID: detail.campaignID,
					campaignName: campaign ? campaign.campaignName : '*** Campaign no longer configured for Drip ***',
					sequence: detail.sequence,
					interval: detail.interval,
					catchup: detail.catchup
				};
			});
		
		// Convert start date string to Date object for UI binding
		if (this.dripProgram.startDate && typeof this.dripProgram.startDate === 'string') {
			this.dripProgram.startDate = new Date(this.dripProgram.startDate);
		}
		
		// Convert delivery time string to Date for UI binding
		if (this.dripProgram.deliveryTime && typeof this.dripProgram.deliveryTime === 'string') {
			const [hours, minutes] = this.dripProgram.deliveryTime.split(':');
			this.deliveryTimeDate = new Date();
			this.deliveryTimeDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
		}
	}

	save() {
		return new Promise<boolean>((resolve, reject) => {
			if (!this.dripProgram || !this.dripProgramID) {
				this.siteService.setMessage('No drip program to save', 'error');
				reject(false);
				return;
			}

			this.loading = true;

			// Convert time back to string format for API
			if (this.deliveryTimeDate) {
				const hours = this.deliveryTimeDate.getHours().toString().padStart(2, '0');
				const minutes = this.deliveryTimeDate.getMinutes().toString().padStart(2, '0');
				this.dripProgram.deliveryTime = `${hours}:${minutes}:00`;
			}

			// Ensure startDate is a proper Date object for JSON serialization
			if (this.dripProgram.startDate && typeof this.dripProgram.startDate === 'string') {
				this.dripProgram.startDate = new Date(this.dripProgram.startDate);
			}

			// Create details array from emails
			const details: DripProgramDetails[] = this.emails.map((email, index) => new DripProgramDetails({
				dripProgramID: this.dripProgramID,
				campaignID: email.campaignID,
				sequence: index + 1,
				interval: email.interval,
				catchup: email.catchup
			}));

			// Save both drip program and details
			forkJoin({
				program: this.dataService.saveDripProgram(this.dripProgram),
				details: this.dataService.saveDripProgramDetails(this.dripProgramID, details)
			}).subscribe({
				next: () => {
					this.siteService.setMessage('Drip program saved successfully');
					this.loading = false;
					this.isDirty = false;
					resolve(true);
				},
				error: (error) => {
					console.error('Error saving drip program:', error);
					this.siteService.setMessage('Error saving drip program', 'error');
					this.loading = false;
					reject(false);
				}
			});
		});
	}

	dropEmail(event: CdkDragDrop<DripProgramEmail[]>) {
		moveItemInArray(this.emails, event.previousIndex, event.currentIndex);
		// Update sequence numbers after reordering
		this.emails.forEach((email, index) => {
			email.sequence = index + 1;
		});
		this.markDirty();
	}

	showAddCampaignDialog() {
		// Get campaigns not already in the sequence
		const usedCampaignIDs = this.emails.map(e => e.campaignID);
		this.filteredAvailableCampaigns = this.availableCampaigns.filter(c => !usedCampaignIDs.includes(c.campaignID));
		
		if (this.filteredAvailableCampaigns.length === 0) {
			this.siteService.setMessage('No more campaigns available to add', 'warn');
			return;
		}

		this.campaignSearchTerm = "";
		this.showCampaignDialog = true;
	}

	filterCampaigns() {
		const usedCampaignIDs = this.emails.map(e => e.campaignID);
		this.filteredAvailableCampaigns = this.availableCampaigns
			.filter(c => !usedCampaignIDs.includes(c.campaignID))
			.filter(c => c.campaignName.toLowerCase().includes(this.campaignSearchTerm.toLowerCase()));
	}

	selectCampaignToAdd(campaign: Campaign) {
		this.addCampaign(campaign.campaignID, campaign.campaignName);
		this.closeCampaignDialog();
	}

	closeCampaignDialog() {
		this.showCampaignDialog = false;
		this.campaignSearchTerm = "";
	}

	addCampaign(campaignID: string, campaignName: string) {
		const newEmail: DripProgramEmail = {
			campaignID: campaignID,
			campaignName: campaignName,
			sequence: this.emails.length + 1,
			interval: 7, // Default to 7 days
			catchup: false
		};
		this.emails.push(newEmail);
		this.markDirty();
	}

	removeCampaign(index: number) {
		this.confirmationService.confirm({
			message: 'Are you sure you want to remove this campaign from the sequence?',
			header: 'Remove Campaign?',
			acceptLabel: 'Remove',
			rejectLabel: 'Cancel',
			acceptIcon: 'check',
			rejectIcon: 'times',
			rejectButtonStyleClass: 'danger',
			accept: () => {
				this.emails.splice(index, 1);
				// Update sequence numbers after removal
				this.emails.forEach((email, idx) => {
					email.sequence = idx + 1;
				});
				this.markDirty();
			}
		});
	}

	markDirty() {
		this.isDirty = true;
	}

	canDeactivate(): boolean | Observable<boolean> {
		return !this.isDirty;
	}
}
